<script setup lang="ts">
import { ref, watch } from 'vue'
import { Upload, X } from 'lucide-vue-next'

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB

interface Props {
  modelValue: File | null
  previewUrl?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  previewUrl: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | null): void
}>()

const error = ref<string | null>(null)
const localPreviewUrl = ref<string | null>(props.previewUrl ?? null)
const fileInputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.previewUrl,
  (val) => {
    if (val) {
      localPreviewUrl.value = val
    }
  },
)

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const files = input.files
  error.value = null

  if (!files || files.length === 0) {
    emit('update:modelValue', null)
    return
  }

  const file = files[0]

  if (!file.type.startsWith('image/')) {
    error.value = 'File must be an image.'
    emit('update:modelValue', null)
    return
  }

  if (file.size > MAX_FILE_SIZE) {
    error.value = 'File size must be under 2MB.'
    emit('update:modelValue', null)
    return
  }

  const prevUrl = localPreviewUrl.value
  localPreviewUrl.value = URL.createObjectURL(file)
  if (prevUrl && prevUrl !== props.previewUrl) {
    URL.revokeObjectURL(prevUrl)
  }

  emit('update:modelValue', file)
}

function triggerUpload() {
  fileInputRef.value?.click()
}

function clearSelection() {
  if (localPreviewUrl.value && localPreviewUrl.value !== props.previewUrl) {
    URL.revokeObjectURL(localPreviewUrl.value)
  }
  localPreviewUrl.value = null
  error.value = null
  emit('update:modelValue', null)
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}
</script>

<template>
  <div class="logo-uploader">
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      class="logo-uploader__input"
      @change="onFileChange"
    />

    <div class="logo-uploader__area" @click="triggerUpload">
      <div v-if="localPreviewUrl" class="logo-uploader__preview-wrapper">
        <img
          :src="localPreviewUrl"
          alt="Logo preview"
          class="logo-uploader__preview"
        />
        <button
          type="button"
          class="logo-uploader__clear"
          @click.stop="clearSelection"
          aria-label="Remove logo"
        >
          <X :size="14" />
        </button>
      </div>
      <div v-else class="logo-uploader__placeholder">
        <Upload :size="24" />
        <span class="logo-uploader__placeholder-text">Click to upload logo</span>
        <span class="logo-uploader__hint">PNG, JPG, GIF up to 2MB</span>
      </div>
    </div>

    <div v-if="error" class="logo-uploader__error">
      {{ error }}
    </div>
  </div>
</template>

<style scoped>
.logo-uploader__input {
  display: none;
}

.logo-uploader__area {
  border: 2px dashed #d6d3d1;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  min-height: 120px;
}

.logo-uploader__area:hover {
  border-color: #a8a29e;
  background: #fafaf9;
}

.logo-uploader__preview-wrapper {
  position: relative;
  display: inline-flex;
}

.logo-uploader__preview {
  max-width: 200px;
  max-height: 120px;
  object-fit: contain;
  border-radius: 6px;
}

.logo-uploader__clear {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #78716c;
  padding: 0;
}

.logo-uploader__clear:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.logo-uploader__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #a8a29e;
}

.logo-uploader__placeholder-text {
  font-size: 14px;
  color: #78716c;
}

.logo-uploader__hint {
  font-size: 12px;
  color: #a8a29e;
}

.logo-uploader__error {
  margin-top: 8px;
  font-size: 13px;
  color: #dc2626;
}
</style>
